/**
 * Dependency-free HTML sanitizer for content bound via `v-safe-html`.
 *
 * Rather than pattern-matching strings (fragile against XSS), it parses the
 * input with the browser's own DOMParser into a detached document and walks the
 * tree, keeping only an allowlist of tags and attributes and dropping anything
 * that can execute script (event handlers, `javascript:` URLs, <script>, etc.).
 *
 * Intended for trusted-but-formatted CMS content (show summaries, artist
 * biographies, notification messages) — not as a general untrusted-input filter.
 */

import { siteOrigin } from '@/config/site'

const ALLOWED_TAGS = new Set([
    'a',
    'b',
    'blockquote',
    'br',
    'div',
    'em',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'hr',
    'i',
    'li',
    'ol',
    'p',
    'span',
    'strong',
    'u',
    'ul',
])

// Attributes allowed per tag. `*` applies to every allowed tag.
const ALLOWED_ATTRIBUTES = {
    '*': ['class'],
    a: ['href', 'target', 'rel'],
}

// Disallowed tags whose contents must be discarded outright rather than
// unwrapped (unwrapping would surface script/style source as visible text).
const DROP_WITH_CONTENT = new Set(['script', 'style', 'iframe', 'object', 'embed', 'template', 'noscript'])

const SAFE_URL_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:'])

function isSafeUrl(value) {
    const trimmed = value.trim()
    // Allow relative URLs and anchors (no protocol).
    if (/^[^:]*$/.test(trimmed) || trimmed.startsWith('/') || trimmed.startsWith('#')) {
        return true
    }
    try {
        // An empty base throws; an absent one does not, and every value reaching
        // here already carries a scheme, so the base only has to not be invalid.
        const { protocol } = new URL(trimmed, siteOrigin() || undefined)
        return SAFE_URL_PROTOCOLS.has(protocol)
    } catch (e) {
        return false
    }
}

function isAttributeAllowed(tagName, attributeName) {
    const allowed = ALLOWED_ATTRIBUTES[tagName] || []
    return ALLOWED_ATTRIBUTES['*'].includes(attributeName) || allowed.includes(attributeName)
}

function sanitizeElement(element) {
    // Iterate over a static copy so removals don't disturb the live collection.
    Array.from(element.children).forEach(child => {
        const tagName = child.tagName.toLowerCase()

        if (DROP_WITH_CONTENT.has(tagName)) {
            child.remove()
            return
        }

        // Sanitize the subtree first, so that if this element itself is
        // unwrapped below, the children promoted into its place are already safe.
        sanitizeElement(child)

        if (!ALLOWED_TAGS.has(tagName)) {
            // Drop the element but keep its (already-sanitized) children.
            const parent = child.parentNode
            while (child.firstChild) {
                parent.insertBefore(child.firstChild, child)
            }
            parent.removeChild(child)
            return
        }

        Array.from(child.attributes).forEach(({ name }) => {
            const attributeName = name.toLowerCase()
            if (!isAttributeAllowed(tagName, attributeName)) {
                child.removeAttribute(name)
                return
            }
            if ((attributeName === 'href' || attributeName === 'src') && !isSafeUrl(child.getAttribute(name))) {
                child.removeAttribute(name)
            }
        })

        // Harden links that open a new tab against reverse-tabnabbing.
        if (tagName === 'a' && child.getAttribute('target') === '_blank') {
            child.setAttribute('rel', 'noopener noreferrer')
        }
    })
}

export default function sanitizeHtml(dirty) {
    if (dirty == null || dirty === '') return ''

    const doc = new DOMParser().parseFromString(String(dirty), 'text/html')
    sanitizeElement(doc.body)
    return doc.body.innerHTML
}
